import { ChatUIContext } from '@/components/context/ChatUIContext';
import {
  downloadCitations,
  ExportFormat,
} from '@/components/report/utils/citation';
import { getCurrentThread } from '@/store/reducers/UIState';
import { getReportStateForThread } from '@/store/reducers/ReportWidgetStore';
import { InlineChipWidget } from '@/components/widgets/InlineChipWidget';
import { InlinePaperChipWidgetProps } from '@/components/widgets/InlinePaperChipWidget';
import { InlinePaperEvidence } from '@/components/widgets/InlinePaperEvidence';
import { LitTable } from '@/components/report/LitTable';
import { PaperDetails, ReportCitation, ReportSection } from '@/models/Report';
import { PaperMetadataString } from '@/components/report/PaperMetadataString';
import { PermissionsContext } from '@/components/context/PermissionsContext';
import LinkWidget from '@/components/widgets/LinkWidget';
// import { ReportSection, ReportSectionFormat } from '@/models/Report';
import { ReportWidgetData } from '@/models/PanelWidgets';
import {
  SectionFeedbackButtons,
  SectionFeedbackType,
} from '@/components/report/SectionFeedbackButtons';
import { useAppSelector } from '@/store/hooks';

import { alpha, Box, Button, Link, Typography } from '@mui/material';
import { ReactNode, useCallback, useContext, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import Markdown, { MarkdownToJSX } from 'markdown-to-jsx';
import Person2Icon from '@mui/icons-material/Person2';
import reactToText from 'react-to-text';
import styled from 'styled-components';

function generateTablePromptForSection(
  section: ReportSection,
  query?: string,
): string {
  return `Create a new paper comparison table for the following papers, corpusIds: ${section.citations
    ?.map((citation) => citation.corpusId)
    .join(', ')}.
    
The comparison table should help answer my initial question "${query}" and focus on comparing the papers on "${
    section.title
  }". The table should contain five relevant suggested aspect columns.`;
}

// const LIST_SECTION_TO_TABLE_CITATION_COUNT_BOUNDS = {
//   min: 3,
//   max: 15,
// };

// function isCitationCountWithinBounds(citationCount: number): boolean {
//   return (
//     citationCount >= LIST_SECTION_TO_TABLE_CITATION_COUNT_BOUNDS.min &&
//     citationCount <= LIST_SECTION_TO_TABLE_CITATION_COUNT_BOUNDS.max
//   );
// }

const REFERENCES_SECTION_ID = 'references';

export const referencesMarkdownOptions: MarkdownToJSX.Options = {
  overrides: {
    p: {
      component: (props) => <Typography {...props} />,
      props: { paragraph: true, variant: 'body1' },
    },
    a: {
      component: (props) => <StyledLink {...props} />,
      props: {
        target: '_blank',
        paragraph: true,
        variant: 'body1',
      },
    },
  },
};

export const Report = (): ReactNode => {
  const currentThread = useAppSelector(getCurrentThread);
  const reportData: ReportWidgetData | null = useAppSelector((state) =>
    getReportStateForThread(state, currentThread?.id),
  );
  const { isReadOnly } = useContext(PermissionsContext);

  const { setSuggestedChatMessage } = useContext(ChatUIContext);
  const handleShowSuggestion = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, suggestion: string) => {
      event.preventDefault();
      setSuggestedChatMessage(suggestion, 0, suggestion.length);
    },
    [setSuggestedChatMessage],
  );

  const [expandedSectionIds, setExpandedSectionIds] = useState<Set<string>>(
    new Set<string>(),
  );

  const handleOnChange = (sectionId: string) => {
    const isCurrentlyExpanded = expandedSectionIds.has(sectionId);
    const updatedExpandedSectionIds = new Set(expandedSectionIds);
    if (isCurrentlyExpanded) {
      updatedExpandedSectionIds.delete(sectionId);
    } else {
      updatedExpandedSectionIds.add(sectionId);
    }
    setExpandedSectionIds(updatedExpandedSectionIds);
  };
  const seen = new Set<number>();
  const citations =
    reportData?.sections
      .filter((section) => section.text && section.citations)
      .map((section) => section.citations)
      .flat()
      .filter((citation) => citation && citation?.paper && citation?.id) ?? [];
  const referencesMarkdown = citations
    .map((citation) => {
      if (!citation || !citation?.id || !citation.paper) {
        return '';
      }
      if (seen.has(citation.paper.corpusId)) {
        return '';
      }
      seen.add(citation.paper.corpusId);
      return `1. ${PaperMetadataString({
        authors: citation.paper.authors,
        title: citation.paper.title,
        venue: citation.paper.venue,
        year: citation.paper.year,
        corpusId: citation.paper.corpusId,
        citationCount: citation.paper.nCitations ?? 0,
      }).trim()}`;
    })
    .filter((m) => m.length > 0);

  const handleDownloadCitations = () => {
    const jsonStringFormat = JSON.stringify(citations, null, 2);
    const formatType: ExportFormat = 'bibtex';
    downloadCitations(jsonStringFormat, formatType);
  };

  const toggleExpand = (sectionId: string) => {
    setExpandedSectionIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  return (
    <div>
      {reportData?.sections?.map((section, idx) => {
        const corpusId2Snippets: { [corpusId: number]: string[] } = {};
        const corpusId2Paper: { [corpusId: number]: PaperDetails } = {};

        // const showTableButton =
        //   section.format === ReportSectionFormat.LIST &&
        //   isCitationCountWithinBounds(section.citations?.length ?? 0);
        section.citations?.forEach((citation) => {
          corpusId2Snippets[citation.corpusId] = citation.snippets;
          if (citation.paper) {
            corpusId2Paper[citation.corpusId] = citation.paper;
          }
        });
        const showTableButton = false;
        const markdownOptions: MarkdownToJSX.Options = {
          overrides: {
            p: {
              component: (props) => <Typography {...props} />,
              props: { paragraph: true, variant: 'body1' },
            },
            Author: {
              component: (props: {
                authorId?: string;
                children?: ReactNode;
              }) => {
                const { authorId, children } = props;
                const authorName = reactToText(children);
                if (authorId) {
                  return (
                    <LinkWidget
                      url={`https://semanticscholar.org/author/${authorId}`}
                    >
                      <InlineChipWidget
                        label={authorName}
                        icon={<Person2Icon />}
                      />
                    </LinkWidget>
                  );
                }
                return (
                  <InlineChipWidget label={authorName} icon={<Person2Icon />} />
                );
              },
            },
            Paper: {
              component: (
                props: {
                  corpusId: number;
                  paperTitle?: string;
                  children?: ReactNode;
                } & Partial<InlinePaperChipWidgetProps>,
              ) => {
                const { corpusId, paperTitle, children } = props;
                let paperTitleStr = paperTitle;
                if (!paperTitleStr && children) {
                  paperTitleStr = reactToText(children);
                }

                if (corpusId && paperTitleStr) {
                  return (
                    <InlinePaperEvidence
                      label={paperTitleStr}
                      paper={corpusId2Paper[corpusId]}
                      corpusId={corpusId}
                      evidence={
                        corpusId2Snippets?.[corpusId]?.map((snippet) => ({
                          text: snippet,
                        })) ?? []
                      }
                    />
                  );
                }
                return null;
              },
            },
            Model: {
              component: (props: { name: string; version: string }) => {
                return (
                  <InlinePaperEvidence
                    label={'(LLM Memory)'}
                    evidence={[
                      {
                        text: `Generated by ${props.name} ${props.version}. We could not find any relevant reference with evidence supporting this statement.`,
                      },
                    ]}
                  />
                );
              },
            },
          },
        };
        const citationId2Citation: { [corpusId: string]: ReportCitation } = {};

        section.citations?.forEach((citation) => {
          if (citation?.paper?.corpusId) {
            citationId2Citation[citation.paper.corpusId] = citation;
          }
        });
        return (
          <AccordionContainer key={`${section.title}-${idx}`}>
            <StyledAccordion
              slotProps={{ heading: { component: 'div' } }}
              expanded={expandedSectionIds.has(section.id)}
              onChange={() => {
                handleOnChange(section.id);
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <div>
                  <Typography variant="h5" component="h5" color="primary">
                    {section.title}
                  </Typography>
                  {section.tldr && !expandedSectionIds.has(section.id) && (
                    <Typography variant="body1" component="p">
                      {section.tldr}
                      {showTableButton && ' (Comparison table available)'}
                    </Typography>
                  )}
                </div>
              </AccordionSummary>
              <AccordionDetails>
                <SectionContainer>
                  {showTableButton && (
                    <Button
                      variant="outlined"
                      color="info"
                      sx={{ mb: 2 }}
                      onClick={(event) =>
                        handleShowSuggestion(
                          event,
                          generateTablePromptForSection(
                            section,
                            reportData.query,
                          ),
                        )
                      }
                    >
                      Create a comparison table for this section
                    </Button>
                  )}
                  <Markdown options={markdownOptions}>{section.text}</Markdown>
                  {!isReadOnly && currentThread?.id && reportData?.id && (
                    <SectionFeedback key={`${section.title}-${idx}-text`}>
                      Is this section helpful?
                      <SectionFeedbackButtons
                        threadId={currentThread.id}
                        widgetId={reportData.id}
                        sectionEventType={SectionFeedbackType.TEXT}
                        sectionName={`${section.title}`}
                        sectionIdx={idx}
                      />
                    </SectionFeedback>
                  )}

                  {/* Table */}
                  {section.table && (section.table?.rows?.length ?? 0) > 1 && (
                    <>
                      <Box
                        sx={{
                          background: 'white',
                          borderRadius: '4px 4px 0 0',
                          padding: '16px',
                        }}
                      >
                        <LitTable
                          table={section.table}
                          corpusId2Citation={citationId2Citation}
                        />
                      </Box>
                      {!isReadOnly && currentThread?.id && reportData?.id && (
                        <SectionFeedback key={`${section.title}-${idx}-text`}>
                          Is this table helpful?
                          <SectionFeedbackButtons
                            threadId={currentThread.id}
                            widgetId={reportData.id}
                            sectionEventType={SectionFeedbackType.TABLE}
                            sectionName={`${section.title}-${idx}`}
                            sectionIdx={idx}
                          />
                        </SectionFeedback>
                      )}
                    </>
                  )}
                </SectionContainer>
              </AccordionDetails>
            </StyledAccordion>
          </AccordionContainer>
        );
      })}
      <AccordionContainer
        style={{ display: referencesMarkdown.length > 0 ? 'block' : 'none' }}
      >
        <StyledReferenceAccordion
          expanded={expandedSectionIds.has(REFERENCES_SECTION_ID)}
          onChange={() => {
            handleOnChange(REFERENCES_SECTION_ID);
          }}
        >
          <AccordionSummary>
            <Typography variant="h6" component="h6" sx={{ lineHeight: '1.5' }}>
              {`${seen.size} paper${
                seen.size > 1 ? 's' : ''
              } cited in this answer.`}
            </Typography>
            <StyledBox>
              {expandedSectionIds.has(REFERENCES_SECTION_ID) && (
                <StyledButton
                  variant="text"
                  size="small"
                  disableRipple
                  startIcon={<FileDownloadIcon />}
                  color="secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownloadCitations();
                  }}
                >
                  Export All Citations
                </StyledButton>
              )}
              {expandedSectionIds.has(REFERENCES_SECTION_ID) && (
                <StyledBulletPoint>•</StyledBulletPoint>
              )}

              <StyledButton
                color="secondary"
                variant="text"
                size="small"
                disableRipple
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpand(REFERENCES_SECTION_ID);
                }}
                endIcon={
                  expandedSectionIds.has(REFERENCES_SECTION_ID) ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )
                }
              >
                {expandedSectionIds.has(REFERENCES_SECTION_ID)
                  ? 'Collapse'
                  : 'Expand'}
              </StyledButton>
            </StyledBox>
          </AccordionSummary>
          <AccordionDetails>
            <Markdown options={referencesMarkdownOptions}>
              {referencesMarkdown.join('\n')}
            </Markdown>
          </AccordionDetails>
        </StyledReferenceAccordion>
      </AccordionContainer>
    </div>
  );
};

const StyledAccordion = styled(Accordion)`
  &.MuiAccordion-root {
    background-color: ${({ theme }) => theme.color['cream-100'].hex};
  }
  .MuiAccordionSummary-root {
    align-items: flex-start;
    gap: 12px;

    .MuiAccordionSummary-expandIconWrapper {
      margin-top: 18px;
    }
  }
  .MuiAccordionSummary-root.Mui-expanded {
    min-height: 0;
  }
  .MuiAccordionSummary-content.Mui-expanded {
    margin-bottom: 12px;
  }
`;

const StyledBox = styled(Box)`
  display: flex;
  align-items: center;
`;

const StyledBulletPoint = styled(Typography)`
  color: rgba(250, 242, 233, 0.6);
  font-size: 1.2rem;
`;

const StyledButton = styled(Button)`
  &:hover {
    background-color: transparent;
  }
`;

const StyledReferenceAccordion = styled(Accordion)`
  &.MuiAccordion-root {
    background: rgba(250, 242, 233, 0.04);
    border: 1px solid rgba(250, 242, 233, 0.1);
  }
  .MuiAccordion-heading {
    .MuiAccordionSummary-content {
      display: flex;
      justify-content: space-between;
    }
    .MuiTypography-h6 {
      color: rgb(250, 242, 233);
    }
  }
  .MuiAccordionDetails-root {
    color: rgb(250, 242, 233);

    ol {
      margin-bottom: 0;
      li {
        margin-bottom: 6px;
      }
    }
  }
`;

const SectionContainer = styled('div')`
  padding: ${({ theme }) => theme.spacing(1.5)};
  background: white;
  border-radius: 6px;
`;

const AccordionContainer = styled('div')`
  margin-bottom: ${({ theme }) => theme.spacing(1.33)};
`;

const SectionFeedback = styled('div')`
  align-items: center;
  background: white;
  border: 1px solid ${({ theme }) => alpha(theme.palette.text.secondary, 0.1)};
  border-radius: 6px;
  color: rgba(240, 82, 156, 1);
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(1, 1.5)};
`;

const StyledLink = styled(Link)`
  &.MuiLink-root {
    color: rgba(15, 203, 140, 1);

    &:hover {
      color: rgba(240, 82, 156, 1);
    }
  }
`;
